import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ExternalLink, Loader2, Pause, Play } from "lucide-react";
import {
  type Device,
  type Sensor,
  getDevice,
  getSensor,
  simulateTick,
  updateSensor,
} from "../../lib/instrumentationApi";
import { formatDate } from "../../lib/utils";
import { SearchableAssetPicker, type AssetOption } from "./SearchableAssetPicker";

interface InlineSensorReadoutProps {
  sensorId: string;
  /** Hint from the parent (the tag's denormalized device_id) to skip an
   *  extra round trip on first load. */
  initialDeviceId?: string | null;
  /** Same-site assets so the user can search + assign without leaving. */
  assets?: AssetOption[];
  /** Color accent for the chart line + readout — matches the tag color. */
  accent?: string;
  /** Polling interval for live updates (ms). Default 2000. */
  pollIntervalMs?: number;
  /** Callback when "Open in Instrumentation" is clicked. Parent owns the
   *  navigation so this component stays router-agnostic and embeddable. */
  onOpenInInstrumentation?: () => void;
  /** Notifies the parent when the user re-links the sensor to a new asset
   *  so the tag list can refresh denormalized fields. */
  onAssetChanged?: () => void;
}

/** Inline live-data widget for a single sensor.
 *
 *  Polls the sensor + (when simulated) ticks the in-app simulator so the
 *  chart breathes. Renders headline reading, a 1-line chart, a few stats,
 *  an asset picker, and an "Open in Instrumentation" link. Designed for
 *  use inside a sidebar row that already shows the tag's name + position,
 *  so it deliberately keeps no header of its own. */
export function InlineSensorReadout({
  sensorId,
  initialDeviceId,
  assets,
  accent = "#22d3ee",
  pollIntervalMs = 2000,
  onOpenInInstrumentation,
  onAssetChanged,
}: InlineSensorReadoutProps) {
  const [sensor, setSensor] = useState<Sensor | null>(null);
  const [device, setDevice] = useState<Device | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  // Asset linkage auto-saves on selection — no separate Save button. While
  // the PUT is in flight we show a small spinner next to the picker and
  // disable it to prevent overlapping writes.
  const [isSavingAsset, setIsSavingAsset] = useState(false);
  // Ref mirror of isSavingAsset so the polling tick (captured in a
  // useCallback closure) sees the up-to-date value without restarting the
  // interval on every render. saveTokenRef bumps on each save start so a
  // tick that began its fetch BEFORE a save can detect that its response
  // is now stale and skip applying it (otherwise an X-button clear would
  // briefly revert to the previous asset).
  const savingRef = useRef(false);
  const saveTokenRef = useRef(0);

  // Initial fetch — pull the sensor + parent device.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    (async () => {
      try {
        const s = await getSensor(sensorId);
        if (cancelled) return;
        setSensor(s);
        const deviceId = s.device_id || initialDeviceId;
        if (deviceId) {
          const d = await getDevice(deviceId);
          if (!cancelled) setDevice(d);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const e = err as { response?: { data?: { detail?: string } } };
        setError(e.response?.data?.detail || "Failed to load sensor.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sensorId, initialDeviceId]);

  const tick = useCallback(async () => {
    if (savingRef.current) return;
    const tokenAtStart = saveTokenRef.current;
    try {
      const fresh =
        device && device.source_type === "simulated"
          ? await simulateTick(sensorId, { count: 1, history_limit: 200 })
          : await getSensor(sensorId);
      // If a save started (or started + completed) during our fetch, the
      // response we just received pre-dates the user's change — applying it
      // would overwrite asset_id back to the stale value.
      if (savingRef.current || saveTokenRef.current !== tokenAtStart) return;
      setSensor(fresh);
    } catch {
      // Swallow per-tick noise.
    }
  }, [device, sensorId]);

  useEffect(() => {
    if (isPaused || !device) return;
    const id = window.setInterval(tick, pollIntervalMs);
    tick();
    return () => window.clearInterval(id);
  }, [isPaused, device, pollIntervalMs, tick]);

  const readings = useMemo(() => {
    const raw = sensor?.data_history?.readings ?? [];
    return raw
      .map((r) => {
        const value = (r as { value?: unknown }).value;
        const ts = (r as { ts?: string }).ts;
        return typeof value === "number"
          ? { value, tsLabel: ts ? new Date(ts).toLocaleTimeString() : "" }
          : null;
      })
      .filter((p): p is { value: number; tsLabel: string } => p !== null);
  }, [sensor]);

  const latest = useMemo(() => {
    const raw = sensor?.data || {};
    const value = (raw as { value?: unknown }).value;
    const ts = (raw as { ts?: string }).ts;
    const unit = sensor?.unit ?? "";
    return {
      value: typeof value === "number" ? value : null,
      ts: ts || null,
      unit,
    };
  }, [sensor]);

  async function handleAssetChange(nextAssetId: string | null) {
    if (!sensor || savingRef.current) return;
    // Skip the round trip when the value didn't actually change. Prevents
    // accidental writes when the picker echoes the current selection back.
    if ((nextAssetId || "") === (sensor.asset_id ?? "")) return;
    // Optimistic update so the picker chip swaps immediately even before
    // the PUT returns. Rolled back on error.
    const before = sensor;
    setSensor({ ...sensor, asset_id: nextAssetId });
    saveTokenRef.current += 1;
    savingRef.current = true;
    setIsSavingAsset(true);
    setError(null);
    try {
      const fresh = await updateSensor(sensor.id, {
        asset_id: nextAssetId,
      });
      setSensor(fresh);
      onAssetChanged?.();
    } catch (err: unknown) {
      setSensor(before);
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail || "Failed to update asset link.");
    } finally {
      savingRef.current = false;
      setIsSavingAsset(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-[11px] text-surface-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading sensor…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded border border-red-500/30 bg-red-500/10 px-2 py-1.5 text-[11px] text-red-200">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {/* Live reading + chart */}
      <div className="rounded-md border border-surface-700/60 bg-surface-900/60 p-2">
        <div className="flex items-center justify-between px-1 pb-1">
          <span className="text-[9px] font-semibold uppercase tracking-wider text-surface-400">
            Live reading
          </span>
          <button
            onClick={() => setIsPaused((v) => !v)}
            className="flex h-5 items-center gap-1 rounded px-1.5 text-[10px] font-semibold uppercase tracking-wider text-surface-300 hover:bg-white/5"
            title={isPaused ? "Resume live updates" : "Pause live updates"}
          >
            {isPaused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
            {isPaused ? "Paused" : "Live"}
          </button>
        </div>
        <div className="flex items-baseline gap-1.5 px-1">
          <span
            className="font-mono text-xl font-semibold"
            style={{ color: accent }}
          >
            {latest.value !== null ? latest.value.toFixed(2) : "—"}
          </span>
          {latest.unit && (
            <span className="text-[10px] text-surface-300">{latest.unit}</span>
          )}
        </div>
        {latest.ts && (
          <div className="px-1 text-[10px] text-surface-500">
            {formatDate(latest.ts)} · {new Date(latest.ts).toLocaleTimeString()}
          </div>
        )}
        {readings.length > 0 ? (
          <div className="mt-1 h-24 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={readings} margin={{ top: 4, right: 4, left: -30, bottom: 0 }}>
                <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                <XAxis
                  dataKey="tsLabel"
                  tick={{ fontSize: 9, fill: "#64748b" }}
                  interval="preserveStartEnd"
                  minTickGap={20}
                />
                <YAxis tick={{ fontSize: 9, fill: "#64748b" }} domain={["auto", "auto"]} width={36} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(15,23,42,0.95)",
                    border: "1px solid #334155",
                    borderRadius: 8,
                    fontSize: 11,
                  }}
                  formatter={(v) => {
                    const n = typeof v === "number" ? v : Number(v);
                    if (!isFinite(n)) return [String(v), "Value"] as [string, string];
                    return [
                      `${n.toFixed(3)}${latest.unit ? ` ${latest.unit}` : ""}`,
                      "Value",
                    ] as [string, string];
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={accent}
                  strokeWidth={1.5}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="mt-1 px-1 py-2 text-center text-[10px] text-surface-500">
            No readings yet.
          </div>
        )}
      </div>

      {/* Status row */}
      <div className="grid grid-cols-2 gap-1.5 text-[10px]">
        <SmallStat label="Source" value={device?.source_type ?? "—"} />
        <SmallStat label="Status" value={sensor?.status ?? "—"} ok={sensor?.status === "active"} />
        <SmallStat
          label="Channel"
          value={sensor?.source_channel ?? sensor?.sensor_type ?? "—"}
        />
        <SmallStat
          label="Connection"
          value={device?.connection_status ?? "—"}
          ok={device?.connection_status === "connected"}
        />
      </div>

      {/* Asset picker — searchable, auto-saves on selection. Shown only
          when same-site assets are provided by the parent. */}
      {assets && (
        <div className="rounded-md border border-surface-700/60 bg-surface-900/40 p-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[9px] font-semibold uppercase tracking-wider text-surface-400">
              Linked asset
            </span>
            {isSavingAsset && (
              <span className="inline-flex items-center gap-1 text-[10px] text-brand-300">
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
                Saving…
              </span>
            )}
          </div>
          <SearchableAssetPicker
            assets={assets}
            value={sensor?.asset_id ?? ""}
            onChange={(id) => handleAssetChange(id)}
            placeholder="Type to search assets (e.g. pump)"
            size="sm"
            disabled={isSavingAsset}
          />
        </div>
      )}

      {/* Footer — deep link into the Instrumentation page. */}
      {onOpenInInstrumentation && (
        <div className="flex justify-end">
          <button
            onClick={onOpenInInstrumentation}
            className="inline-flex items-center gap-1 text-[11px] text-brand-300 hover:text-brand-200"
            title="Open this sensor on the Instrumentation page"
          >
            <ExternalLink className="h-3 w-3" />
            Open in Instrumentation
          </button>
        </div>
      )}
    </div>
  );
}

function SmallStat({
  label,
  value,
  ok,
}: {
  label: string;
  value: string;
  ok?: boolean;
}) {
  return (
    <div className="rounded border border-surface-700/40 bg-surface-900/40 px-1.5 py-1">
      <div className="text-[9px] font-semibold uppercase tracking-wider text-surface-500">
        {label}
      </div>
      <div
        className={
          "mt-0.5 truncate text-[10px] font-medium " +
          (ok ? "text-emerald-300" : "text-surface-200")
        }
      >
        {value}
      </div>
    </div>
  );
}
